import { DivProps } from '@commonTypes'
import React, { useEffect, useReducer, useState } from 'react'
import omit from 'lodash/omit'
import gql from 'graphql-tag'
import { RouteComponentProps } from '@reach/router'
import { navigateWithBasepath as navigate, legacyMode } from 'App'
import Appbar from 'components/Appbar'
import { useQuery } from 'react-apollo'
import config from './config'
import { StackedBar, DonutChart, Bar, HeatMap } from './charts'
import StatsBar from './StatsBar'
import Summary from './SummaryTable'
import Table from './ReportTable'
import Filters, { IFilter } from './ReportFilters'
import { IReportSection } from '@api'
import { CHART_TYPES, GROUP_TYPES, IGroup, IReportFilters, ITooltip } from './types'
import { PageContent, IconButton, Flex, Button, FilterButton } from '@ui'
import { DateRange } from '@blueprintjs/datetime'
import { Collapse } from '@blueprintjs/core'
import ReportSkeleton from './ReportSkeleton'
import { useTrackNavigate, useFileDownload } from '@hooks'
import track from '@track'
// import moment from 'moment'
import { DateUtil } from '@utils'
import { Toaster } from '@ui'
import { navigateToAphrodite } from '@utils/navigateToAphrodite'
import css from './reportPage.module.scss'
import ReportPageContent from '@ui/ReportPageContent'

interface IProps {
  id: string
  spaceId?: string
}

interface IFullReport {
  name: string
  id: string
  categoryName: string
  filters: IFilter[]
  sections: IReportSection[]
  groups: IGroup[]
  tooltips: ITooltip[]
}

interface IDepartment {
  id: string
  color: string
}
interface IPayload {
  viewer: {
    company: {
      departments: IDepartment[]
      report: IFullReport
    }
  }
}

export enum IActionType {
  INIT,
  FILTER,
  DATE_RANGE_FILTER,
  START_YEAR_CHANGE_FILTER,
  END_YEAR_CHANGE_FILTER,
  RESET,
}
interface IActInit {
  type: IActionType.INIT
  payload: any
}

interface IActFilter {
  type: IActionType.FILTER
  payload: {
    category: string
    value: any
  }
}

interface IDateRangeFilter {
  type: IActionType.DATE_RANGE_FILTER
  payload: {
    range: DateRange
  }
}

interface IStartYearChangeFilter {
  type: IActionType.START_YEAR_CHANGE_FILTER
  payload: {
    startYear: string
  }
}

interface IEndYearChangeFilter {
  type: IActionType.END_YEAR_CHANGE_FILTER
  payload: {
    endYear: string
  }
}

interface IFiltersReset {
  type: IActionType.RESET
}

export type IActions =
  | IActInit
  | IActFilter
  | IDateRangeFilter
  | IFiltersReset
  | IStartYearChangeFilter
  | IEndYearChangeFilter

const reducer: React.Reducer<IReportFilters, IActions> = (state, action) => {
  switch (action.type) {
    case IActionType.INIT:
      return {
        ...state,
        ...action.payload,
      }
    case IActionType.FILTER:
      return {
        ...state,
        [action.payload.category]: action.payload.value,
      }

    case IActionType.DATE_RANGE_FILTER:
      return {
        ...state,
        fromDate: DateUtil(action.payload.range[0] ?? undefined).format('yyyy-MM-dd'),
        toDate: DateUtil(action.payload.range[1] ?? undefined).format('yyyy-MM-dd'),
      }

    case IActionType.START_YEAR_CHANGE_FILTER:
      const startYear = action.payload.startYear
      const endYear = startYear + 9
      return {
        ...state,
        fromDate: `${startYear}-01-01`,
        toDate: `${endYear}-12-31`,
        calculateForYear:
          state.calculateForYear &&
          (state.calculateForYear < Number(startYear) || state.calculateForYear > Number(endYear))
            ? Number(endYear)
            : undefined,
      }

    case IActionType.END_YEAR_CHANGE_FILTER:
      return {
        ...state,
        toDate: `${action.payload.endYear}-12-31`,
        calculateForYear:
          state.calculateForYear && state.calculateForYear > Number(action.payload.endYear)
            ? Number(action.payload.endYear)
            : state.calculateForYear,
      }

    case IActionType.RESET:
      return {}

    default:
      return state
  }
}

const PER_PAGE = 25

export default function ReportPage({ id, spaceId, path }: IProps & RouteComponentProps) {
  useTrackNavigate()
  useEffect(() => window.scrollTo(0, 0), [path])
  const [isPaging, setPaging] = useState<boolean>(false)
  const [orderBy, setOrderBy] = useState<string>('')
  const [descending, setDescending] = useState<boolean>(false)
  const [nextPage, setNextPage] = useState<number>(2)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [reportFilters, dispatch] = useReducer(reducer, {})
  const [showFilters, setShowFilters] = useState(true)
  const startFileDownload = useFileDownload()

  useEffect(() => {
    track.reportPage({
      eventName: track.Event.NAVIGATE,
      reportName: id,
      props: { pageName: `${id} report` },
    })
  }, [id])

  useEffect(() => {
    setHasMore(true)
    setNextPage(2)
  }, [reportFilters, orderBy, descending])

  const { data, loading, fetchMore, client, variables } = useQuery<IPayload>(reportQuery, {
    variables: {
      name: id,
      order: orderBy ? orderBy : undefined,
      desc: descending,
      spaceId,
      perPage: PER_PAGE,
      ...omit(reportFilters, 'employees'),
      assignmentPlanId: reportFilters.assignmentPlan ? reportFilters.assignmentPlan.id : null,
      costCenterId: reportFilters.costCenterId ? reportFilters.costCenterId.map((cc) => cc.id) : [],
      globalMapObjectId: reportFilters.globalMapObjectId
        ? reportFilters.globalMapObjectId.map((gmo) => gmo.id)
        : [],
      buildingId: reportFilters.buildingId ? reportFilters.buildingId.map((b) => b.id) : [],
      floorId: reportFilters.floorId ? reportFilters.floorId.map((b) => b.id) : [],
      departmentId: reportFilters.departmentId ? reportFilters.departmentId.map((d) => d.id) : [],
      spaceTypeId: reportFilters.spaceTypeId ? reportFilters.spaceTypeId.map((st) => st.id) : [],
      usageTypeId: reportFilters.usageTypeId ? reportFilters.usageTypeId.map((ut) => ut.id) : [],
      site: reportFilters.site ? reportFilters.site.map((st) => st.id) : [],
      calculateForYear: reportFilters.calculateForYear,
      employeeId: reportFilters.employees?.map((employees) => employees.id) || [],
      isCancelled: !!reportFilters.isCancelled,
      page: 1,
    },
  })

  const sortBy = (column: string) => {
    if (column === orderBy) {
      if (descending) {
        setDescending(false)
        track.reportPage({
          eventName: track.Event.SORT,
          reportName: report.id,
          props: {
            columnName: column,
          },
        })
      } else {
        setDescending(true)
        track.reportPage({
          reportName: report.id,
          eventName: track.Event.SORT,
          props: {
            columnName: column,
          },
        })
      }
    } else {
      setDescending(false)
      setOrderBy(column)
      track.reportPage({
        reportName: report.id,
        eventName: track.Event.SORT,
        props: {
          columnName: column,
        },
      })
    }
  }

  const reportConfig = config[id] || config.default
  if ((loading && !data) || !data || !data.viewer) {
    return <ReportSkeleton />
  }

  const getDownloadLink = () => {
    client
      .query({
        query: downloadQuery,
        variables: { ...variables, allRecords: true, perPage: undefined, page: undefined },
      })
      .then((data) => {
        startFileDownload(data.data.viewer.company.report.csvUrl)
        track.reportPage({
          reportName: report.id,
          eventName: track.Event.EXPORT,
          props: {
            reportId: id,
          },
        })
      })
  }
  const { report, departments: depts } = data.viewer.company
  if (!report) {
    Toaster.fail({ message: 'Oops, something went wrong' })
    return null
  }
  const { groups, sections, filters, tooltips } = report
  const stats = groups.filter((g) => g.type === GROUP_TYPES.STATS_TABLE)[0]
  const summary = groups.find((g) => g.type === GROUP_TYPES.SUMMARY_TABLE)
  const charts = groups.filter((g) => g.type === GROUP_TYPES.CHART)
  const table = groups.find((g) => g.type === GROUP_TYPES.TABLE)
  const tableFooter = groups.find((g) => g.type === GROUP_TYPES.TABLE_FOOTER)

  const fetchMoreRows = () => {
    setPaging(true)
    return fetchMore({
      query: reportQuery,
      variables: { ...variables, page: nextPage },
      updateQuery: (prev: IPayload, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.viewer) {
          return prev
        }

        const {
          viewer: {
            company: {
              report: { groups: newGroups },
            },
          },
        } = fetchMoreResult

        const newTable = newGroups.find((gr) => gr.type === GROUP_TYPES.TABLE)

        if (newTable) {
          const newRows = newTable.values
          const oldGroups = prev.viewer.company.report.groups.filter(
            (gr) => gr.type !== GROUP_TYPES.TABLE
          )
          const oldTable = prev.viewer.company.report.groups.find(
            (gr) => gr.type === GROUP_TYPES.TABLE
          )
          if (oldTable) {
            const { values: oldValues } = oldTable
            const newGroup: IGroup = {
              ...oldTable,
              values: [...oldValues, ...newRows],
            }
            setNextPage(nextPage + 1)
            if (newRows.length < PER_PAGE) {
              setHasMore(false)
            }
            setPaging(false)
            return {
              ...prev,
              viewer: {
                ...prev.viewer,
                company: {
                  ...prev.viewer.company,
                  report: {
                    ...prev.viewer.company.report,
                    groups: [...oldGroups, newGroup],
                  },
                },
              },
            }
          } else {
            setPaging(false)
            return prev
          }
        } else {
          setPaging(false)
          return prev
        }
      },
    })
  }

  const filterCount = getFilterCount(reportFilters, reportConfig)

  return (
    <>
      <Appbar
        title={
          <Flex align="center">
            <span style={{ marginRight: 8 }}>
              <IconButton
                minimal
                iconName="chevron-left"
                onClick={() => {
                  navigate && navigate(`/reports/${encodeURIComponent(report.categoryName)}`, {})
                }}
              />
            </span>
            {report.name}
          </Flex>
        }
      />
      <div style={{ marginBottom: 14, paddingLeft: 24, paddingRight: 24 }}>
        <Flex
          justify={!!filters.length ? 'space-between' : 'flex-end'}
          style={{ padding: '10px 0' }}>
          {!!filters.length && (
            <Flex align="center">
              <FilterButton
                filterCount={filterCount}
                onClick={() => setShowFilters(!showFilters)}
              />
              {!!filterCount && (
                <Flex align="baseline">
                  <span style={{ paddingLeft: 10, color: 'var(--grey1)' }}>filtered results</span>
                  <IconButton
                    minimal
                    round
                    tooltip="Clear filters"
                    iconName="times"
                    style={{ marginLeft: 4 }}
                    onClick={() => {
                      track.reportPage({
                        reportName: report.id,
                        eventName: track.Event.CLEAR_ALL_FILTERS,
                      })
                      dispatch({ type: IActionType.RESET })
                    }}
                  />
                </Flex>
              )}
            </Flex>
          )}
          <Button iconName="download" text="Export" onClick={getDownloadLink} />
        </Flex>
        <Collapse keepChildrenMounted isOpen={!!filters.length && showFilters}>
          <div style={{ marginBottom: 10 }}>
            <Filters
              reportName={report.id}
              reportConfig={reportConfig}
              requiredFilters={filters}
              dispatchFilter={dispatch}
              reportFilters={reportFilters}
              bottomMargin={!!charts.length}
            />
          </div>
        </Collapse>
      </div>
      <ReportPageContent isFiltered={showFilters}>
        {!!stats && <StatsBar stats={stats} />}
        {!!summary && <Summary summary={summary} />}

        {!!charts.length && (
          <div className={css.row}>
            {charts.map((chart, i) => {
              const chartConf = reportConfig[chart.name]
              let colors: any
              const onItemClick: ((item: any) => void) | undefined =
                chartConf && chartConf.filter && chartConf.payloadToFilter
                  ? (data) => {
                      track.reportPage({
                        reportName: report.id,
                        eventName: track.Event.CHART_CLICK,
                        props: { chartName: chart.name },
                      })
                      const converted = chartConf.payloadToFilter(data)
                      if (converted)
                        dispatch({
                          type: IActionType.FILTER,
                          payload: {
                            category: chartConf.filter,
                            value: [converted],
                          },
                        })
                    }
                  : undefined

              if (/department/i.test(chart.name)) {
                colors = depts.reduce<{ [key: string]: string }>((result, { id, color }) => {
                  result[id] = color
                  return result
                }, {})
              }

              switch (chart.chartType) {
                case CHART_TYPES.DONUT:
                  if (!chartConf) {
                    return <ChartNotSupported name={chart.name} />
                  }
                  return (
                    <DonutChart
                      key={chart.name}
                      chart={chart}
                      conf={chartConf}
                      height={332}
                      onClick={onItemClick}
                    />
                  )
                case CHART_TYPES.HORIZONTAL_BAR:
                  if (!chartConf) {
                    return <ChartNotSupported name={chart.name} />
                  }
                  return (
                    <Bar
                      key={`bar-chart-${i}`}
                      chart={chart}
                      layout="horizontal"
                      config={chartConf}
                      colors={colors}
                      height={332}
                      onClick={onItemClick}
                    />
                  )
                case CHART_TYPES.VERTICAL_BAR:
                  if (!chartConf) {
                    return <ChartNotSupported name={chart.name} />
                  }
                  return (
                    <Bar
                      key={chart.name}
                      chart={chart}
                      layout="vertical"
                      config={chartConf}
                      colors={colors}
                      height={332}
                      onClick={onItemClick}
                    />
                  )
                case CHART_TYPES.SBAR:
                  if (!chartConf) {
                    return <ChartNotSupported name={chart.name} />
                  }
                  return (
                    <StackedBar
                      key={chart.name}
                      chart={chart}
                      config={chartConf}
                      layout="vertical"
                      height={332}
                      onClick={onItemClick}
                    />
                  )
                case CHART_TYPES.HEATMAP: {
                  return <HeatMap chart={chart} key={chart.name} config={chartConf} height={332} />
                }
                default:
                  return <div />
              }
            })}
          </div>
        )}
        {!!table &&
          // only Financial forecast reports don't need sticky headers
          // because this report has 2 tables
          (id.includes('Financial') || id.includes('HotelDeskBookingReport') ? (
            <div className="table-responsive ">
              <Table
                sections={sections}
                table={table}
                orderBy={orderBy}
                descending={descending}
                sortBy={sortBy}
                nextPage={fetchMoreRows}
                showLoadMore={table.values.length >= PER_PAGE && hasMore}
                isPaging={isPaging}
                config={reportConfig}
                isSorting={loading}
                tooltips={tooltips}
                footer={tableFooter}
              />
            </div>
          ) : (
            <Table
              sections={sections}
              table={table}
              orderBy={orderBy}
              descending={descending}
              sortBy={sortBy}
              nextPage={fetchMoreRows}
              showLoadMore={table.values.length >= PER_PAGE && hasMore}
              isPaging={isPaging}
              config={reportConfig}
              isSorting={loading}
              tooltips={tooltips}
              footer={tableFooter}
            />
          ))}
      </ReportPageContent>
    </>
  )
}

function getFilterCount(reportFilters: IReportFilters, config: any) {
  let skipDateFilter = false
  let count = 0

  Object.keys(reportFilters).forEach((key) => {
    if (key === 'assignmentPlan') {
      if (reportFilters.assignmentPlan && reportFilters.assignmentPlan.name !== 'Current') {
        count++
      }
    } else if (key === 'fromDate' || key === 'toDate') {
      const fromDate = reportFilters.fromDate
      const toDate = reportFilters.toDate

      if (
        !skipDateFilter &&
        config.defaultDateFilter &&
        fromDate &&
        toDate &&
        (fromDate.toString() !==
          DateUtil(config.defaultDateFilter.dateRange[0].toString()).format('yyyy-MM-dd') ||
          toDate.toString() !==
            DateUtil(config.defaultDateFilter.dateRange[1].toString()).format('yyyy-MM-dd'))
      ) {
        // count date filter only once
        skipDateFilter = true
        count++
      }
      if (
        !skipDateFilter &&
        config.defaultYearFilter &&
        fromDate &&
        toDate &&
        fromDate !== `${config.defaultYearFilter}-01-01`
      ) {
        // count date filter only once
        skipDateFilter = true
        count++
      }
    }
    /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    else if (reportFilters[key] && reportFilters[key].length) {
      count++
    }
  })

  return count
}

const ChartNotSupported = ({ name }: { name: string }) => (
  <div key={name}>
    <p>
      Chart not supported - <strong>{name}</strong>
    </p>
  </div>
)

export const Column: React.FC<React.PropsWithChildren<DivProps>> = ({ className, ...props }) => {
  return <div className={css.column} {...props} />
}

const reportQuery = gql`
  query reportDetails(
    $name: String!
    $spaceId: String
    $assignmentPlanId: String
    $departmentId: [String]
    $city: [String]
    $buildingId: [String]
    $floorId: [String]
    $spaceTypeId: [String]
    $usageTypeId: [String]
    $employmentType: [String]
    $globalMapObjectId: [String]
    $site: [String]
    $costCenterId: [String]
    $size: [String]
    $desc: Boolean
    $order: String
    $search: String
    $page: Int
    $perPage: Int
    $fromDate: String
    $toDate: String
    $region: [String]
    $country: [String]
    $calculateForYear: Int
    $employeeId: [String]
    $isCancelled: Boolean
  ) {
    viewer {
      company {
        departments {
          color
          id
        }
        report(
          name: $name
          spaceId: $spaceId
          assignmentPlanId: $assignmentPlanId
          departmentId: $departmentId
          city: $city
          buildingId: $buildingId
          floorId: $floorId
          spaceTypeId: $spaceTypeId
          usageTypeId: $usageTypeId
          employmentType: $employmentType
          globalMapObjectId: $globalMapObjectId
          site: $site
          costCenterId: $costCenterId
          size: $size
          desc: $desc
          order: $order
          search: $search
          page: $page
          perPage: $perPage
          fromDate: $fromDate
          toDate: $toDate
          region: $region
          country: $country
          calculateForYear: $calculateForYear
          employeeId: $employeeId
          isCancelled: $isCancelled
        ) {
          id
          name
          categoryName
          filters {
            id
            name
          }
          sections {
            name
            span
          }
          groups {
            columns
            columnTypes
            name
            type
            chartType
            values
            tooltip
          }
          tooltips {
            name
            description
          }
        }
      }
    }
  }
`

const downloadQuery = gql`
  query reportDetails(
    $name: String!
    $spaceId: String
    $assignmentPlanId: String
    $departmentId: [String]
    $city: [String]
    $buildingId: [String]
    $floorId: [String]
    $spaceTypeId: [String]
    $usageTypeId: [String]
    $employmentType: [String]
    $globalMapObjectId: [String]
    $site: [String]
    $costCenterId: [String]
    $size: [String]
    $desc: Boolean
    $order: String
    $search: String
    $allRecords: Boolean
    $page: Int
    $perPage: Int
    $fromDate: String
    $toDate: String
    $region: [String]
    $country: [String]
    $calculateForYear: Int
    $employeeId: [String]
    $isCancelled: Boolean
  ) {
    viewer {
      company {
        departments {
          color
          id
        }
        report(
          name: $name
          spaceId: $spaceId
          assignmentPlanId: $assignmentPlanId
          departmentId: $departmentId
          city: $city
          buildingId: $buildingId
          floorId: $floorId
          spaceTypeId: $spaceTypeId
          usageTypeId: $usageTypeId
          employmentType: $employmentType
          globalMapObjectId: $globalMapObjectId
          site: $site
          costCenterId: $costCenterId
          size: $size
          desc: $desc
          order: $order
          search: $search
          allRecords: $allRecords
          page: $page
          perPage: $perPage
          fromDate: $fromDate
          toDate: $toDate
          region: $region
          country: $country
          calculateForYear: $calculateForYear
          employeeId: $employeeId
          isCancelled: $isCancelled
        ) {
          id
          csvUrl
        }
      }
    }
  }
`
